import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import useAuthStore from '@/stores/authStore';
import { Button, Input, Container } from '@components';

// Validation schema
const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

const ForgotPassword = () => {
  const { forgotPassword, loading } = useAuthStore();
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data) => {
    try {
      await forgotPassword(data.email);
      setEmailSent(true);
    } catch (error) {
      console.error('Forgot password error:', error);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-soft-gray">
        <Container className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-card p-8 text-center">
            {/* Success Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-action-success-green bg-opacity-10 mb-6">
              <svg
                className="h-8 w-8 text-action-success-green"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h2 className="text-h4 text-neutral-charcoal mb-4">
              Check your email
            </h2>
            <p className="text-body text-neutral-medium-gray mb-6">
              We've sent password reset instructions to{' '}
              <span className="font-semibold text-neutral-charcoal">
                {getValues('email')}
              </span>
            </p>
            <p className="text-small text-neutral-medium-gray mb-8">
              Didn't receive the email? Check your spam folder or{' '}
              <button
                onClick={() => setEmailSent(false)}
                className="text-primary-teal hover:text-primary-purple font-medium"
              >
                try again
              </button>
            </p>

            <Link to="/login">
              <Button variant="primary" className="w-full">
                Back to login
              </Button>
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-soft-gray">
      <Container className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-h3 text-primary-navy mb-2">DataLogicHub</h1>
            <h2 className="text-h4 text-neutral-charcoal mb-2">
              Forgot password?
            </h2>
            <p className="text-body text-neutral-medium-gray">
              No worries, we'll send you reset instructions
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              type="email"
              label="Email"
              placeholder="you@example.com"
              error={errors.email?.message}
              {...register('email')}
              required
            />

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              loading={loading}
              disabled={loading}
            >
              Send reset instructions
            </Button>
          </form>

          {/* Back to login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-medium text-neutral-medium-gray hover:text-neutral-charcoal"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to login
            </Link>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ForgotPassword;
